/**
 * Algolia Analytics
 * Handles conversion tracking for add to cart events
 */
(function() {
  'use strict';

  const config = window.algoliaThemeConfig || {};

  /**
   * Store search attribution data for conversion tracking
   * This ONLY stores the click attribution - it doesn't send an event yet.
   * The actual conversion event is sent by Shopify's web pixel when add-to-cart happens.
   *
   * @param {string} queryID - Algolia query ID
   * @param {string} objectID - Variant/Object ID
   * @param {string|number} productID - Product ID
   * @param {string} indexName - Algolia index name
   */
  function storeSearchAttribution(queryID, objectID, productID, indexName) {
    if (!queryID || !objectID) {
      config.log?.('Skip storing attribution - missing queryID or objectID');
      return;
    }

    const clickData = {
      eventName: 'Added to cart', // Metadata for web pixel - not sending event now
      index: indexName || 'shopify_products',
      queryID: queryID,
      objectIDs: [objectID],
      productId: String(productID),
      timestamp: Date.now(),
    };

    try {
      const localStorageKey = 'algolia_analytics_clicked_objects';
      const previousClickItemsString = localStorage.getItem(localStorageKey) || '[]';
      const previousClickItems = JSON.parse(previousClickItemsString);

      // Add new click data
      previousClickItems.push(clickData);

      // Keep only last 10 items to prevent localStorage bloat
      const recentItems = previousClickItems.slice(-10);

      localStorage.setItem(localStorageKey, JSON.stringify(recentItems));
      config.log?.('✓ Click attribution stored (variant:', objectID, ')');
      config.log?.('  → Will be used for add-to-cart event if product is purchased');
    } catch (error) {
      config.error?.('Failed to store search attribution:', error);
    }
  }

  /**
   * Retrieve attribution data for a specific variant (without clearing)
   * The Shopify web pixel will read this data when add-to-cart event fires
   * @param {string|number} variantID - Variant ID to look up
   * @returns {Object|null} Attribution data or null
   */
  function getSearchAttribution(variantID) {
    try {
      const localStorageKey = 'algolia_analytics_clicked_objects';
      const clickItemsString = localStorage.getItem(localStorageKey) || '[]';
      const clickItems = JSON.parse(clickItemsString);

      // Find matching attribution by objectID (variant ID)
      const attribution = clickItems.find(item =>
        item.objectIDs && item.objectIDs.includes(String(variantID))
      );

      if (attribution) {
        config.log?.('Retrieved attribution:', attribution);
        return attribution;
      }

      return null;
    } catch (error) {
      config.error?.('Failed to retrieve search attribution:', error);
      return null;
    }
  }

  /**
   * Clean up old attribution data (older than 1 hour)
   * Prevents localStorage from growing indefinitely
   */
  function cleanupOldAttribution() {
    try {
      const localStorageKey = 'algolia_analytics_clicked_objects';
      const clickItemsString = localStorage.getItem(localStorageKey) || '[]';
      const clickItems = JSON.parse(clickItemsString);

      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      const recentItems = clickItems.filter(item =>
        item.timestamp && item.timestamp > oneHourAgo
      );

      if (recentItems.length !== clickItems.length) {
        localStorage.setItem(localStorageKey, JSON.stringify(recentItems));
        config.log?.('Cleaned up old attribution data');
      }
    } catch (error) {
      config.error?.('Failed to clean up attribution:', error);
    }
  }

  /**
   * Track product click from search results
   * Stores attribution data for later conversion tracking
   */
  function trackProductClick(event) {
    const $target = $(event.currentTarget);
    const $productCard = $target.closest('[data-algolia-queryid]');

    if (!$productCard.length) return;

    const queryID = $productCard.data('algolia-queryid');
    const objectID = $productCard.data('algolia-objectid');
    const productID = $productCard.data('product-id');
    const indexName = $productCard.data('algolia-index');

    storeSearchAttribution(queryID, objectID, productID, indexName);
  }

  /**
   * Inject data-insights-filter onto refinement list items after the Algolia
   * Shopify app renders them. Reads the facet attribute name from the closest
   * wrapper with data-insights-attribute and the value from the checkbox.
   */
  function injectFilterAttributes(root) {
    root.querySelectorAll('.ais-RefinementList-item:not([data-insights-filter])').forEach(function(item) {
      const label = item.querySelector('.ais-RefinementList-label');
      let value = null;
      if (label) {
        const clone = label.cloneNode(true);
        clone.querySelectorAll('input, span').forEach(function(el) { el.remove(); });
        value = clone.textContent.trim() || null;
      }
      const wrapper = item.closest('[data-insights-attribute]');
      const attribute = wrapper ? wrapper.getAttribute('data-insights-attribute') : null;
      if (attribute && value) {
        item.setAttribute('data-insights-filter', attribute + ':' + value);
      }
    });
  }

  function initFilterAttributeInjection() {
    const facetsContainer = document.querySelector('.ais-facets');
    if (!facetsContainer) {
      setTimeout(initFilterAttributeInjection, 300);
      return;
    }
    injectFilterAttributes(facetsContainer);
    new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) injectFilterAttributes(node);
        });
      });
    }).observe(facetsContainer, { childList: true, subtree: true });
    config.log?.('Filter attribute injection initialized');
  }

  /**
   * Push Algolia user token to GTM dataLayer whenever it changes.
   * GTM's Algolia Search Insights template loads window.aa (search-insights);
   * we poll until it's available then register the onChange callback.
   */
  function syncUserTokenToDataLayer() {
    if (typeof window.aa === 'function') {
      window.aa('onUserTokenChange', function(userToken) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ algoliaUserToken: userToken });
        config.log?.('User token pushed to dataLayer:', userToken);
      }, { immediate: true });
    } else {
      setTimeout(syncUserTokenToDataLayer, 300);
    }
  }

  // Initialize when jQuery is available
  function init() {
    if (!window.jQuery) {
      setTimeout(init, 100);
      return;
    }

    const $ = window.jQuery;

    // Track clicks on product links from search results
    $(document).on('click', '.ais-Hits-item .product-link', trackProductClick);

    // Track clicks on quick buy buttons with capture phase to run before modal handler
    document.addEventListener('click', function(e) {
      const $target = $(e.target);
      const $quickbuy = $target.closest('.ais-Hits-item .quickbuy-toggle');

      if ($quickbuy.length) {
        config.log?.('📦 Quick buy button clicked - storing attribution');
        trackProductClick({ currentTarget: $quickbuy[0] });
      }
    }, true); // Use capture phase to run before other handlers

    // Clean up old attribution data periodically
    cleanupOldAttribution();
    setInterval(cleanupOldAttribution, 5 * 60 * 1000); // Every 5 minutes

    // Inject data-insights-filter attributes onto filter items for GTM tracking
    initFilterAttributeInjection();

    // Sync Algolia user token to GTM dataLayer once search-insights (loaded by GTM) is ready
    syncUserTokenToDataLayer();

    config.log?.('Algolia Analytics initialized');
  }

  // Start initialization
  init();

  // Expose functions globally for manual tracking if needed
  window.algoliaAnalytics = {
    storeSearchAttribution: storeSearchAttribution,
    getSearchAttribution: getSearchAttribution,
    trackProductClick: trackProductClick,
    cleanupOldAttribution: cleanupOldAttribution,
    syncUserTokenToDataLayer: syncUserTokenToDataLayer,
  };
})();
